import { UserRoutingStrategy, RoutingContext, NavigationDestination } from '../types';
import { User } from '@/services/platformApi';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';

/**
 * Default routing strategy for regular users
 */
export class DefaultRoutingStrategy implements UserRoutingStrategy {
  priority = 5; // Medium priority - handle non-merchant/non-admin users

  canHandle(user: User): boolean {
    // This strategy handles users who are NOT merchants or admins
    // ONLY check roles array - this is the authoritative source
    const isMerchant = user.roles?.includes('MERCHANT');
    const isAdmin = user.roles?.includes('ADMIN') || user.roles?.includes('SUPPORT');
    
    // Only handle regular users (not merchants or admins)
    return !isMerchant && !isAdmin;
  }

  async getDestination(context: RoutingContext): Promise<NavigationDestination> {
    const { user, returnUrl, fromSignup } = context;

    // If there's a specific return URL, respect it
    if (returnUrl && this.isValidReturnUrl(returnUrl)) {
      return { path: returnUrl };
    }

    // For new signups, might want to show an onboarding flow
    if (fromSignup) {
      // Check if user has any incomplete profile fields
      const needsProfileCompletion = this.checkProfileCompletion(user);
      
      if (needsProfileCompletion) {
        return {
          path: '/profile',
          state: { 
            fromSignup: true,
            showWelcome: true
          }
        };
      }
    }

    // Check if user has specific preferences
    const userPreferences = this.getUserPreferences(user);
    if (userPreferences?.defaultPage) {
      return { 
        path: userPreferences.defaultPage,
        state: { fromLogin: context.fromLogin }
      };
    }

    return {
      path: getDefaultAuthenticatedDestination(),
      state: {
        fromLogin: context.fromLogin,
        userId: user._id,
      },
    };
  }

  private checkProfileCompletion(user: User): boolean {
    // Check if essential profile fields are missing
    return !(
      user.firstName && 
      user.lastName && 
      user.email
    );
  }

  private getUserPreferences(user: User): any {
    // Could fetch from localStorage or user preferences
    try {
      const stored = localStorage.getItem(`userPrefs_${user._id}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private isValidReturnUrl(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch {
      return url.startsWith('/') && !url.startsWith('//');
    }
  }
}
